/**
 * Explore Search Overlay - Full-screen search with animated results
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { ExploreWeaviateSearchApi } from '../../Model/Explore/ExploreWeaviateSearch';
import { ExploreWeaviateSearchResult } from '../../Model/Explore/ExploreWeaviateSearchResult';
import { ExploreSearchResults } from './ExploreSearchResults';
import { useTrans } from '../../Store/System/LocalizationStore';
import { ExploreEntryBuilder } from '../../Model/Explore/ExploreEntryBuilder';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { subtreeCategoryIdsOfAsync } from '../../Model/Explore/exploreCategoryTreeV3';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 1; }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  z-index: 1000;
  overflow-x: hidden;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const SearchHeader = styled.div`
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  padding: 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  z-index: 10;
  animation: ${slideDown} 0.4s ease-out;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  font-size: 1.5rem;
  font-weight: 500;
  border: none;
  background: transparent;
  outline: none;
  padding: 12px 0;
  color: #1a1a2e;

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 600px) {
    font-size: 1.2rem;
  }
`;

const CloseButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #f6f6f7;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #666;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #e0e0e0;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #aaa;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;

  &:hover {
    color: #666;
  }
`;

const SearchIcon = styled.div`
  font-size: 24px;
  color: #00dda2;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ResultsContainer = styled.div`
  padding: 32px 16px;
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const LoadingDot = styled.div<{ delay: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00dda2, #00AF96);
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}ms;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #888;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 12px 0;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  color: #666;
  max-width: 400px;
  line-height: 1.6;
`;

const ResultsCount = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 24px;
  animation: ${slideDown} 0.4s ease-out;
`;

const SuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const SuggestionChip = styled.button`
  padding: 10px 20px;
  border-radius: 24px;
  border: 1px solid #e0e0e0;
  background: white;
  font-size: 0.9rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #00dda2;
    color: #00dda2;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.85rem;
  }
`;

export type ExploreSearchOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
  linkPrefix?: string;
  /** When set, uses category-filtered search instead of Weaviate semantic search */
  categoryFilter?: string;
  /** Label for the category filter (shown in placeholder) */
  categoryLabel?: string;
};

export const ExploreSearchOverlay: React.FC<ExploreSearchOverlayProps> = observer(
  ({ isOpen, onClose, linkPrefix = '/academy/', categoryFilter, categoryLabel }) => {
    const { t } = useTrans();
    const inputRef = useRef<HTMLInputElement>(null);

    const suggestions = useMemo(
      () => [
        t('search.suggestion.feelTired', { defaultValue: 'I feel tired' }),
        t('search.suggestion.quickWorkout', { defaultValue: 'Quick workout' }),
        t('search.suggestion.healthyRecipes', { defaultValue: 'Healthy recipes' }),
        t('search.suggestion.stressRelief', { defaultValue: 'Stress relief' }),
        t('search.suggestion.betterSleep', { defaultValue: 'Better sleep' }),
        t('search.suggestion.morningRoutine', { defaultValue: 'Morning routine' }),
      ],
      [t],
    );
    const [query, setQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [results, setResults] = useState<ExploreWeaviateSearchResult | null>(null);
    const [categoryResults, setCategoryResults] = useState<ExploreEntry[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const entryAbortRef = useRef<AbortController | null>(null);

    const isCategorySearch = !!categoryFilter;

    useEffect(() => {
      if (isOpen && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) {
        setQuery('');
        setResults(null);
        setCategoryResults([]);
        setHasSearched(false);
        ExploreWeaviateSearchApi.abort();
        if (entryAbortRef.current) {
          entryAbortRef.current.abort();
          entryAbortRef.current = null;
        }
      }
    }, [isOpen]);

    const performSearch = useCallback(
      async (searchQuery: string) => {
        if (searchQuery.trim().length < 2) {
          setResults(null);
          setCategoryResults([]);
          setHasSearched(false);
          return;
        }

        setIsSearching(true);
        setHasSearched(true);

        try {
          if (isCategorySearch && categoryFilter) {
            // Abort previous entry search
            if (entryAbortRef.current) {
              entryAbortRef.current.abort();
            }
            const abortController = new AbortController();
            entryAbortRef.current = abortController;

            // The V3 entries endpoint has no text-search param and `parentId` only
            // matches DIRECT children, so fetch the whole category subtree and apply
            // the typed text as a client-side filter. An unresolvable slug yields []
            // (no results) rather than the unfiltered catalog.
            const subtreeIds = await subtreeCategoryIdsOfAsync(categoryFilter);
            const perCategory = await Promise.all(
              subtreeIds.map((id) => ExploreEntryBuilder.find({ rootCategories: [id], size: 50 }, abortController.signal)),
            );
            const needle = searchQuery.trim().toLowerCase();
            const seen = new Set<string>();
            const entries = perCategory
              .flat()
              .filter((entry) => (seen.has(entry.id) ? false : (seen.add(entry.id), true)))
              .filter((entry) =>
                [entry.name, entry.shortDescription, entry.description]
                  .flat()
                  .some((value) => value?.value?.toLowerCase().includes(needle)),
              );
            setCategoryResults(entries);
            setResults(null);
          } else {
            // Use Weaviate semantic search
            const result = await ExploreWeaviateSearchApi.search({
              concepts: [searchQuery.trim()],
              autocut: 5,
            });
            setResults(result);
            setCategoryResults([]);
          }
        } catch (error: any) {
          if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
            console.error('Search error:', error);
          }
        } finally {
          setIsSearching(false);
        }
      },
      [isCategorySearch, categoryFilter],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);

        // Debounce search
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
          performSearch(value);
        }, 400);
      },
      [performSearch],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        } else if (e.key === 'Enter') {
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
          }
          performSearch(query);
        }
      },
      [onClose, performSearch, query],
    );

    const handleSuggestionClick = useCallback(
      (suggestion: string) => {
        setQuery(suggestion);
        performSearch(suggestion);
      },
      [performSearch],
    );

    const handleClear = useCallback(() => {
      setQuery('');
      setResults(null);
      setHasSearched(false);
      inputRef.current?.focus();
    }, []);

    return (
      <Overlay isOpen={isOpen}>
        <SearchHeader>
          <Container>
            <SearchInputContainer>
              <SearchIcon>🔍</SearchIcon>
              <SearchInput
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  categoryLabel
                    ? t('exploreSearch.categoryPlaceholder', {
                        defaultValue: 'Search in {{category}}...',
                        category: categoryLabel,
                      })
                    : t('exploreSearch.placeholder', { defaultValue: 'Search for anything...' })
                }
              />
              {query.length > 0 && (
                <ClearButton onClick={handleClear} title="Clear">
                  <i className="bi bi-x-lg" />
                </ClearButton>
              )}
              <CloseButton onClick={onClose} title="Close (Esc)">
                <i className="bi bi-x" />
              </CloseButton>
            </SearchInputContainer>
          </Container>
        </SearchHeader>

        <ResultsContainer>
          <Container>
            {isSearching && (
              <LoadingContainer>
                <LoadingDots>
                  <LoadingDot delay={0} />
                  <LoadingDot delay={200} />
                  <LoadingDot delay={400} />
                </LoadingDots>
                <LoadingText>{t('exploreSearch.searching', { defaultValue: 'Searching...' })}</LoadingText>
              </LoadingContainer>
            )}

            {!isSearching && !hasSearched && (
              <EmptyStateContainer>
                <EmptyStateIcon>💡</EmptyStateIcon>
                <EmptyStateTitle>{t('exploreSearch.startTitle', { defaultValue: 'Smart Search' })}</EmptyStateTitle>
                <EmptyStateText>
                  {t('exploreSearch.startDescription', {
                    defaultValue:
                      'Start typing to discover content that matches your interests. Try natural language like "I feel tired" to find resources.',
                  })}
                </EmptyStateText>
                <SuggestionChips>
                  {suggestions.map((suggestion) => (
                    <SuggestionChip key={suggestion} onClick={() => handleSuggestionClick(suggestion)}>
                      {suggestion}
                    </SuggestionChip>
                  ))}
                </SuggestionChips>
              </EmptyStateContainer>
            )}

            {!isSearching && hasSearched && !isCategorySearch && results && results.isEmpty && (
              <EmptyStateContainer>
                <EmptyStateIcon>🔍</EmptyStateIcon>
                <EmptyStateTitle>
                  {t('exploreSearch.noResultsTitle', { defaultValue: 'No results found' })}
                </EmptyStateTitle>
                <EmptyStateText>
                  {t('exploreSearch.noResultsDescription', {
                    defaultValue: 'Try different keywords or check your spelling.',
                  })}
                </EmptyStateText>
              </EmptyStateContainer>
            )}

            {!isSearching && hasSearched && isCategorySearch && categoryResults.length === 0 && (
              <EmptyStateContainer>
                <EmptyStateIcon>🔍</EmptyStateIcon>
                <EmptyStateTitle>
                  {t('exploreSearch.noResultsTitle', { defaultValue: 'No results found' })}
                </EmptyStateTitle>
                <EmptyStateText>
                  {t('exploreSearch.noResultsInCategory', {
                    defaultValue: 'No matching content found in this category. Try different keywords.',
                  })}
                </EmptyStateText>
              </EmptyStateContainer>
            )}

            {!isSearching && !isCategorySearch && results && !results.isEmpty && (
              <>
                <ResultsCount>
                  {t('exploreSearch.resultsCount', {
                    defaultValue: '{{count}} results found',
                    count: results.totalCount,
                  })}
                </ResultsCount>
                <ExploreSearchResults results={results} linkPrefix={linkPrefix} onItemClick={onClose} />
              </>
            )}

            {!isSearching && isCategorySearch && categoryResults.length > 0 && (
              <>
                <ResultsCount>
                  {t('exploreSearch.resultsCount', {
                    defaultValue: '{{count}} results found',
                    count: categoryResults.length,
                  })}
                </ResultsCount>
                <ExploreSearchResults categoryEntries={categoryResults} linkPrefix={linkPrefix} onItemClick={onClose} />
              </>
            )}
          </Container>
        </ResultsContainer>
      </Overlay>
    );
  },
);
